import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ListGroup, Image, Spinner, Row, Col, Form, InputGroup, Button, Pagination } from 'react-bootstrap';
import useToken from 'hooks/useToken';

const ModalCambiarVendedor = ({ show, onHide, onSelect, currentVendedorId }) => {
    const { token, setToken } = useToken();
    const [loading, setLoading] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [filter, setFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    useEffect(() => {
        if (show) {
            fetchUsuarios();
        }
    }, [show]);

    useEffect(() => {
        // reset to first page when filter or pageSize changes
        setCurrentPage(1);
    }, [filter, pageSize]);

    const fetchUsuarios = async () => {
        setLoading(true);
        try {
            const ep = '/api/orden/ObtenerVendedores';
            const response = await fetch(ep, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                }
                setUsuarios([]);
                setLoading(false);
                return;
            }
            else {
                const data = await response.json();
                if (data.iResponseType == 1) {
                    console.log(data.detalles["table1"]);
                    setUsuarios(JSON.parse(data.detalles).table2);
                    }

            }

        } catch (error) {
            console.error('Error fetching vendedores:', error);
            setUsuarios([]);
        } finally {
            setLoading(false);
        }
    };

    const getImageSrc = (u) => {
        if (u && u.vFoto) {
            const s = u.vFoto;
            if (s.startsWith('data:')) return s;
            return `data:image/png;base64,${s}`;
        }
        return '/img/team/nofoto50.png';
    };

    const handleSelect = (usuario) => {
        if (onSelect) onSelect(usuario);
        onHide();
    };

    const filtered = usuarios.filter(u => (u._displayName || '').toLowerCase().includes(filter.toLowerCase()));

    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);
    const pageItems = filtered.slice(startIndex, endIndex);

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const items = [];
        const maxButtons = 7;
        let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let end = start + maxButtons - 1;
        if (end > totalPages) { end = totalPages; start = Math.max(1, end - maxButtons + 1); }

        items.push(
            <Pagination.Prev key="prev" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} />
        );

        if (start > 1) {
            items.push(<Pagination.Item key={1} onClick={() => setCurrentPage(1)}>{1}</Pagination.Item>);
            if (start > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
        }

        for (let number = start; number <= end; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        if (end < totalPages) {
            if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>);
        }

        items.push(
            <Pagination.Next key="next" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} />
        );

        return <Pagination size="sm">{items}</Pagination>;
    };

    return (
        <Modal show={show} onHide={onHide} size="md" centered>
            <Modal.Header closeButton>
                <Modal.Title>Cambiar Vendedor</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="mb-2">
                    <Col xs={8}>
                        <InputGroup>
                            <Form.Control
                                placeholder="Buscar vendedor..."
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                size="sm"
                            />
                            <Button variant="outline-secondary" size="sm" onClick={() => setFilter('')}>Limpiar</Button>
                        </InputGroup>
                    </Col>
                    <Col xs={4} className="d-flex justify-content-end align-items-center">
                        <Form.Select size="sm" value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))} style={{ width: 100 }}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </Form.Select>
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" /> Cargando vendedores...
                    </div>
                ) : (
                    <>
                        <ListGroup variant="flush">
                            {pageItems.length === 0 && <ListGroup.Item className="text-muted">No se encontraron vendedores</ListGroup.Item>}
                            {pageItems.map((u, idx) => (
                                <ListGroup.Item key={idx} action onClick={() => handleSelect(u)} className="d-flex align-items-center">
                                    <Image src={getImageSrc(u)} roundedCircle width={40} height={40} className="me-2" onError={(e) => { e.target.onerror = null; e.target.src = '/img/team/nofoto50.png'; }} />
                                    <div className="flex-1">
                                        <div className="fw-bold">{u.vNombreCompleto}</div>
                                    </div>
                                    {String(u.idUsuario) === String(currentVendedorId) && (
                                        <div className="badge bg-primary text-white ms-auto">Actual</div>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>

                        <Row className="mt-2 align-items-center">
                            <Col xs={6} className="fs--2 text-600">Mostrando {startIndex + 1} - {endIndex} de {totalItems}</Col>
                            <Col xs={6} className="d-flex justify-content-end">{renderPagination()}</Col>
                        </Row>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

ModalCambiarVendedor.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    currentVendedorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ModalCambiarVendedor;
